import { ADMIN_ROLE_NAME } from "@app/shared"
import { AppShell, Input, Sidebar, Text, Topbar } from "@app/ui"
import type { TopbarUser } from "@app/ui"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Outlet, createRootRoute, useLocation } from "@tanstack/react-router"
import { Navigate } from "@tanstack/react-router"
import { type Session, signOut } from "../api/auth"
import { getOnboardingStatus, onboardingQueryKeys } from "../api/onboarding"
import { sessionQueryKey, useSession } from "../auth/session"
import { AppNav } from "../components/AppNav"
import { OnboardingLockedScreen } from "../components/OnboardingLockedScreen"
import { ScopeSelector } from "../components/ScopeSelector"

export const Route = createRootRoute({
	component: RootLayout,
})

/**
 * Sidebar brand mark — the current RAM-OS brand mark (dark rounded square,
 * paper-tone rounded outline, green diagonal + centre dot) plus the wordmark.
 * Matches the favicon, the sign-in lockup, and the OTP email header. The mark
 * is a self-contained app-icon, so it reads on both the dark and light
 * sidebar (`bg-base` = #18191B / #EFEBE6); the square sits one step off the
 * dark rail and reads as an icon on the light one.
 */
const BrandMark = (
	<span style={{ display: "inline-flex", alignItems: "center", gap: "9px" }}>
		<svg
			width="28"
			height="28"
			viewBox="0 0 40 40"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			role="img"
			aria-label="RAM-OS"
		>
			<rect
				x="0.6"
				y="0.6"
				width="38.8"
				height="38.8"
				rx="8.4"
				fill="#22242A"
				stroke="#F1EEEB"
				strokeOpacity="0.10"
				strokeWidth="1.2"
			/>
			<rect
				x="11"
				y="11"
				width="18"
				height="18"
				rx="5.5"
				fill="none"
				stroke="#F1EEEB"
				strokeWidth="2.4"
			/>
			<path d="M20 20 L26.5 13.5" stroke="#6FAB8D" strokeWidth="2" strokeLinecap="round" />
			<circle cx="20" cy="20" r="3.6" fill="#6FAB8D" />
		</svg>
		<span style={{ fontWeight: 700, fontSize: "16px", letterSpacing: "0.01em" }}>
			RAM<span style={{ color: "var(--gm-color-brand-9)" }}>·</span>OS
		</span>
	</span>
)

/**
 * Stable, themed full-page frame shown while the session and
 * onboarding-status queries resolve on a cold refresh. Rendering ONE
 * consistent frame (instead of a bare `<Outlet/>`, `null`, or a tiny
 * unstyled "Loading…") is what kills the refresh flash: a hard reload
 * always refetches both queries, and the old code flipped through
 * blank → "Loading…" → shell in between. The brand mark makes the
 * frame read as intentional rather than broken.
 */
const FullPageLoader = (
	<div
		style={{
			minHeight: "100vh",
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
			justifyContent: "center",
			gap: "14px",
			background: "var(--gm-color-bg-base)",
		}}
	>
		{BrandMark}
		<Text variant="body-sm" tone="muted">
			Loading…
		</Text>
	</div>
)

const TOPBAR_SEARCH = (
	<Input inputSize="sm" placeholder="Search shipments, BL numbers, parties…" aria-label="Search" />
)

/**
 * Admin role check for the onboarding gate. Reads `session.roles[]`
 * surfaced from the engine's Auth.js session callback (which copies
 * the role names from the JWT). Non-admins (or sessions with no
 * roles) see the OnboardingLockedScreen until an admin completes the
 * wizard.
 */
function isAdminFromSession(session: Session): boolean {
	return session.roles.includes(ADMIN_ROLE_NAME)
}

export function RootLayout() {
	const { data: session, isLoading } = useSession()
	const location = useLocation()
	const queryClient = useQueryClient()
	const signOutMutation = useMutation({
		mutationFn: () => signOut(),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: sessionQueryKey })
		},
	})
	const onboardingStatus = useQuery({
		queryKey: onboardingQueryKeys.status(),
		queryFn: ({ signal }) => getOnboardingStatus(signal),
		enabled: Boolean(session),
		// Don't retry the 401/423 case forever — the gate already
		// branches on session-existence above.
		retry: 1,
	})

	// Session request still in flight (cold refresh): `data` is
	// `undefined`, not yet `null`. Hold the stable loader. Returning
	// `<Outlet/>` here — as the old `!session` check did while loading —
	// renders route content (or fires per-page `Navigate to="/sign-in"`
	// guards) before we know whether a session exists, which is the
	// refresh flash the user sees.
	if (isLoading) return FullPageLoader

	// `data === null`: the server confirmed there's no session. Funnel every
	// unauthenticated route to /sign-in (except the sign-in route itself, to
	// avoid a redirect loop). Previously this rendered a bare <Outlet/> and
	// relied on per-page `Navigate to="/sign-in"` guards that don't exist — so a
	// logged-out visitor landed on a route whose data just 401'd, with no login.
	if (!session) {
		const here = location.pathname
		if (here === "/sign-in" || here.startsWith("/sign-in/")) return <Outlet />
		return <Navigate to="/sign-in" />
	}

	const path = location.pathname
	const inOnboarding = path === "/onboarding" || path.startsWith("/onboarding/")
	const inSignIn = path === "/sign-in" || path.startsWith("/sign-in/")

	// Authenticated, but sitting on /sign-in (a just-completed OTP verify, a
	// refresh, or a back-button). Bounce to the app root; the onboarding gate
	// below then routes them correctly. Without this the AppShell renders with
	// the SignInPage still inside the <Outlet/>.
	if (inSignIn) return <Navigate to="/" />

	// Onboarding status still resolving (runs once the session exists):
	// hold the SAME stable frame so the cold-refresh sequence is a single
	// transition — loader → app — rather than loader → bare frame → app.
	if (onboardingStatus.isPending) return FullPageLoader

	const incomplete = onboardingStatus.data?.completedAt === null
	if (incomplete && !inOnboarding && !inSignIn) {
		if (isAdminFromSession(session)) return <Navigate to="/onboarding" />
		return <OnboardingLockedScreen />
	}

	// Authenticated + onboarded: render the full app shell around the
	// routed content. The unauthenticated pass-through and the
	// OnboardingLockedScreen branch above are intentionally left bare.
	// Build the topbar user object conditionally so `email` is only set when
	// present (helm runs `exactOptionalPropertyTypes`, which rejects an
	// explicit `string | undefined` against the optional `email?: string`).
	const topbarUser: TopbarUser = { onSignOut: () => signOutMutation.mutate() }
	if (session.user.email) topbarUser.email = session.user.email

	return (
		<AppShell
			sidebar={<Sidebar brand={BrandMark}>{<AppNav />}</Sidebar>}
			topbar={
				<Topbar
					title="Operations control"
					search={TOPBAR_SEARCH}
					scope={<ScopeSelector />}
					user={topbarUser}
				/>
			}
		>
			<Outlet />
		</AppShell>
	)
}
